import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useFetcher, useNavigate } from '@remix-run/react';
import { withZod } from '@remix-validated-form/with-zod';
import { EmailChecked } from '~/components/images/EmailChecked';
import PasswordValidation from '~/components/password-validation';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent } from '~/components/ui/dialog';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { PasswordInput } from '~/components/ui/password-input';
import { useToast } from '~/components/ui/use-toast';
import { LoginInput, loginValidator } from '~/services/models/auth';
import { EmailInput, emailValidator, ResetPasswordInput, resetPasswordValidator } from '~/services/models/users';
import { FetcherData } from '~/types';
import { ChevronLeft, LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { ValidatedForm } from 'remix-validated-form';

const loginValidation = withZod(loginValidator);
const forgotPasswordValidation = withZod(emailValidator);
const resetPasswordValidation = withZod(resetPasswordValidator);

interface LoginProps {
  asSeller?: boolean;
  openLoginDialog?: boolean;
  setOpenLoginDialog?: (value: boolean) => void;
  openLoginSellerDialog?: boolean;
  setOpenLoginSellerDialog?: (value: boolean) => void;
  onSwitchToRegister?: () => void;
}

export default function Login({
  asSeller,
  openLoginDialog,
  setOpenLoginDialog,
  openLoginSellerDialog,
  setOpenLoginSellerDialog,
  onSwitchToRegister,
}: LoginProps) {
  const fetcher = useFetcher();
  const navigate = useNavigate();

  const [step, setStep] = useState<string>('login');

  const toast = useToast();

  const loginForm = useForm<LoginInput>({
    resolver: zodResolver(loginValidator),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const forgotPasswordForm = useForm<EmailInput>({
    resolver: zodResolver(emailValidator),
    defaultValues: {
      email: '',
    },
  });

  const resetPasswordForm = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordValidator),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    loginForm.setValue('isSeller', String(!!asSeller));
  }, [asSeller]);

  useEffect(() => {
    const fetcherData = fetcher.data as FetcherData;
    if (fetcherData?.status === 'success' && fetcherData?.action === 'login') {
      toast.toast({
        title: 'Success',
        description: 'You have successfully logged in.',
      });

      if (asSeller) {
        setOpenLoginSellerDialog?.(false);
        navigate('/dashboard');
      } else {
        setOpenLoginDialog?.(false);
      }
    } else if (fetcherData?.status === 'success' && fetcherData?.action === 'forgot-password') {
      setStep('checkEmail');
    }

    if (fetcherData?.error) {
      toast.toast({
        variant: 'destructive',
        title: 'Error',
        description:
          typeof fetcherData?.error === 'string' ? fetcherData?.error : 'Something went wrong. Please try again',
      });
    }
  }, [fetcher.data]);

  const loginItem = (
    <ValidatedForm validator={loginValidation} method="post" action="/api/login" fetcher={fetcher}>
      <Form {...loginForm}>
        <input type="hidden" name="isSeller" value={String(!!asSeller)} />

        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center justify-center">
            <p className="text-xl font-medium text-black">
              {asSeller ? 'Login to Best2bid Vendor Portal' : 'Login to Best2bid'}
            </p>
            <p className="text-sm font-normal text-slate-500">Please enter your login details to continue</p>
          </div>
          <div className="my-4 flex flex-col gap-2">
            <div className="w-full items-center gap-1.5">
              <FormField
                control={loginForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="mb-2">
                    <FormLabel>Email</FormLabel>
                    <Input type="email" id="email" placeholder="Email" {...field} />
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>
            <div className="w-full items-center gap-1.5">
              <FormField
                control={loginForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="mb-2">
                    <FormLabel>Password</FormLabel>
                    <PasswordInput id="password" placeholder="Password" {...field} />
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-end justify-end">
              <span
                className="cursor-pointer p-0 text-xs font-normal text-blue-600"
                onClick={() => {
                  setStep('forgotPassword');
                }}
              >
                Forgot Password
              </span>
            </div>
          </div>

          <Button type="submit" variant={'primary'} disabled={fetcher.state === 'submitting'}>
            {fetcher.state === 'submitting' && <LoaderCircle className="mr-2 animate-spin" />}
            Login
          </Button>
          {!asSeller && (
            <div className="flex flex-row items-center justify-center gap-2 text-sm font-semibold">
              <p className="text-slate-700">New here?</p>
              <Button
                variant="link"
                className="px-0 text-blue-600"
                onClick={(e) => {
                  e.preventDefault();
                  onSwitchToRegister?.();
                }}
              >
                Create Account
              </Button>
            </div>
          )}
          <p className="text-xs font-normal text-slate-500">
            By continuing you agree to the <span className="text-blue-600">terms of service</span> and{' '}
            <span className="text-blue-600">privacy policy</span>. We may send you communications; you can set your
            preferences in your account.
          </p>
        </div>
      </Form>
    </ValidatedForm>
  );

  const forgotPassword = (
    <ValidatedForm validator={forgotPasswordValidation} method="post" action="/api/forgot-password" fetcher={fetcher}>
      <Form {...forgotPasswordForm}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center justify-center">
            <div
              onClick={(e) => {
                e.preventDefault();
                setStep('login');
              }}
              className="flex cursor-pointer items-center gap-x-1 self-start"
            >
              <ChevronLeft className="h-4 w-4" color="#64748b" />
              <p className="text-sm font-normal text-slate-500">Back</p>
            </div>
            <p className="text-xl font-medium text-black">Reset your password</p>
            <p className="text-sm font-normal text-slate-500">
              We&apos;ll send you instructions on how to reset your password to your email
            </p>
          </div>
          <div className="my-4 flex flex-col gap-2">
            <div className="w-full items-center gap-1.5">
              <FormField
                control={forgotPasswordForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="mb-2">
                    <FormLabel>Email</FormLabel>
                    <Input placeholder="Please input your email" {...field} />
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <Button
            type="submit"
            variant={'primary'}
            onClick={(e) => {
              forgotPasswordForm.trigger();
            }}
            disabled={fetcher.state === 'submitting'}
          >
            {fetcher.state === 'submitting' && <LoaderCircle className="mr-2 animate-spin" />}
            Reset Password
          </Button>
        </div>
      </Form>
    </ValidatedForm>
  );

  const checkEmail = (
    <div className="mt-4 flex flex-col items-center justify-center gap-4">
      <EmailChecked />
      <p className="text-xl font-medium text-black">Check your email</p>
      <p className="text-sm font-normal text-slate-600">
        An email has been sent to <span className="text-black">{forgotPasswordForm.getValues('email')}</span> with
        instructions on how to reset your password
      </p>
      <Button
        variant={'primary'}
        className="w-full"
        onClick={(e) => {
          e.preventDefault();
          if (asSeller) {
            setOpenLoginSellerDialog?.(false);
          } else {
            setOpenLoginDialog?.(false);
          }
          setStep('login');
        }}
      >
        Close
      </Button>
    </div>
  );

  const resetPassword = (
    <ValidatedForm validator={resetPasswordValidation} method="post" action="/api/reset-password" fetcher={fetcher}>
      <Form {...resetPasswordForm}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center justify-center">
            <p className="text-xl font-medium text-black">Create New Password</p>
            <p className="text-sm font-normal text-slate-500">Make sure it is different from the previous password</p>
          </div>
          <div className="my-4 flex flex-col gap-2">
            <div className="w-full items-center gap-1.5">
              <FormField
                control={resetPasswordForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="mb-2">
                    <FormLabel>New Password</FormLabel>
                    <PasswordInput placeholder="Please input your new password" {...field} />
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>
            <PasswordValidation control={resetPasswordForm.control} />
          </div>
          <Button variant={'primary'}>Reset Password</Button>
        </div>
      </Form>
    </ValidatedForm>
  );

  return (
    <Dialog
      open={asSeller ? openLoginSellerDialog : openLoginDialog}
      onOpenChange={asSeller ? setOpenLoginSellerDialog : setOpenLoginDialog}
    >
      {asSeller ? (
        <Button
          variant="ghost"
          className="text-md text-[#3358d4]"
          onClick={() => setOpenLoginSellerDialog && setOpenLoginSellerDialog(true)}
        >
          Login as Seller
        </Button>
      ) : (
        <Button variant="blue" className="text-md" onClick={() => setOpenLoginDialog && setOpenLoginDialog(true)}>
          Login
        </Button>
      )}
      <DialogContent className="max-h-screen overflow-auto sm:max-w-[700px]">
        {step === 'login'
          ? loginItem
          : step === 'forgotPassword'
            ? forgotPassword
            : step === 'checkEmail'
              ? checkEmail
              : resetPassword}
      </DialogContent>
    </Dialog>
  );
}
